import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { Button } from "../../../components/ui/button";

import { TrancheInput } from "./TrancheInput";

export interface TrancheRow {
    /** Threshold amount. `null` = empty (not yet typed) — rendered blank. */
    amount: number | null;
    /** Reward multiplier. `null` = empty (not yet typed) — rendered blank. */
    multiplier: number | null;
}

export interface TrancheListProps {
    /**
     * Optional heading rendered above the rows. Omitted in the current
     * Programs flow (the policy title is rendered by the caller).
     */
    label?: string;
    /** The tranche rows to render. */
    tranches: TrancheRow[];
    /** aria-label for each row's amount field — the domain unit's label. */
    amountLabel: string;
    /**
     * Trailing adornment for each row's amount field — the unit decoration
     * (e.g. a "days" label or a "USDv" token chip). A slot, so this list never
     * imports a domain unit.
     */
    amountAdornment: React.ReactNode;
    onAmountChange: (index: number, amount: number | null) => void;
    onMultiplierChange: (index: number, multiplier: number | null) => void;
    onRemove: (index: number) => void;
    onAdd: () => void;
    /**
     * Sim-only cursor-key prefix. Each row's amount field gets
     * `${prefix}-amount-${i}` and its multiplier field `${prefix}-mult-${i}`, so
     * a scripted cursor can travel to a SPECIFIC row's amount OR multiplier
     * (querySelector matches the first — the keys must be unique per row/field).
     * Inert in production. When omitted, no cursor keys are placed.
     */
    rowCursorKeyPrefix?: string;
    /** Sim-only — the row whose AMOUNT field paints its forced visual, or null. */
    activeAmountRow?: number | null;
    /** Sim-only — the forced phase (hover→border, press→focus) on the active
     *  amount field. */
    amountRowPhase?: "hover" | "press" | null;
    /** Sim-only — the row whose MULTIPLIER field paints its forced visual. */
    activeMultiplierRow?: number | null;
    /** Sim-only — the forced phase on the active multiplier field. */
    multiplierRowPhase?: "hover" | "press" | null;
    /** Sim-only cursor key placed on the "+Add Tranche" button. */
    addCursorKey?: string;
    /** Sim-only — force the "+Add Tranche" button hover visual. */
    addForceHover?: boolean;
    /** Sim-only — force the "+Add Tranche" button pressed visual. */
    addForcePressed?: boolean;
}

/**
 * TrancheList — an optional label, N `TrancheInput` rows, and a "+Add Tranche"
 * button that appends a row.
 *
 * Generic Component: the row unit (days vs USDv) enters as the
 * `amountAdornment` slot; the caller owns parse/serialize. Pure controlled
 * View.
 */
export function TrancheList({
    label,
    tranches,
    amountLabel,
    amountAdornment,
    onAmountChange,
    onMultiplierChange,
    onRemove,
    onAdd,
    rowCursorKeyPrefix,
    activeAmountRow = null,
    amountRowPhase = null,
    activeMultiplierRow = null,
    multiplierRowPhase = null,
    addCursorKey,
    addForceHover = false,
    addForcePressed = false
}: TrancheListProps): React.ReactElement {
    return (
        <div className="flex flex-col gap-2">
            {label && (
                <Text
                    variant="label"
                    render={<span />}
                    className="text-foreground-muted"
                >
                    {label}
                </Text>
            )}
            {tranches.map((t, i) => {
                const amountActive = activeAmountRow === i;
                const multActive = activeMultiplierRow === i;
                return (
                    <TrancheInput
                        key={i}
                        amount={t.amount}
                        multiplier={t.multiplier}
                        amountLabel={amountLabel}
                        amountAdornment={amountAdornment}
                        onAmountChange={(amount) => onAmountChange(i, amount)}
                        onMultiplierChange={(multiplier) =>
                            onMultiplierChange(i, multiplier)
                        }
                        onRemove={() => onRemove(i)}
                        // Per-row cursor keys so the scripted cursor can travel to
                        // THIS row's amount / multiplier (unique per row/field).
                        amountCursorKey={
                            rowCursorKeyPrefix
                                ? `${rowCursorKeyPrefix}-amount-${i}`
                                : undefined
                        }
                        amountForceHover={
                            amountActive && amountRowPhase === "hover"
                        }
                        amountForceFocus={
                            amountActive && amountRowPhase === "press"
                        }
                        multiplierCursorKey={
                            rowCursorKeyPrefix
                                ? `${rowCursorKeyPrefix}-mult-${i}`
                                : undefined
                        }
                        multiplierForceHover={
                            multActive && multiplierRowPhase === "hover"
                        }
                        multiplierForceFocus={
                            multActive && multiplierRowPhase === "press"
                        }
                    />
                );
            })}
            <div className="mt-1">
                <Button
                    variant="transparent"
                    interaction="underline"
                    size="sm"
                    onClick={onAdd}
                    data-sim-cursor-key={addCursorKey}
                    forceHover={addForceHover}
                    forcePressed={addForcePressed}
                >
                    +Add Tranche
                </Button>
            </div>
        </div>
    );
}
